import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Download, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const STATUS_LABEL = {
  never: { label: 'ไม่เคยขายเลย', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  stale: { label: 'หยุดขายมา >2 ปี', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  active: { label: 'ยังขายอยู่', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
};

const TAB_OPTIONS = [
  { key: 'never', label: 'ไม่เคยขายเลย', icon: XCircle, color: 'text-red-600' },
  { key: 'stale', label: 'หยุดขายมา >2 ปี', icon: AlertTriangle, color: 'text-yellow-600' },
  { key: 'active', label: 'ยังขายอยู่', icon: CheckCircle, color: 'text-green-600' },
];

const COLUMNS = [
  { key: 'item_code', label: 'รหัสสินค้า' },
  { key: 'item_name', label: 'ชื่อสินค้า' },
  { key: 'group_product', label: 'กลุ่ม' },
  { key: 'stock', label: 'Stock' },
  { key: 'invoice_count', label: 'ใบขาย' },
  { key: 'total_sold', label: 'ขายได้' },
  { key: 'last_sale_date', label: 'ขายล่าสุด' },
];

const UnsoldProductsComponent = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('never');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'stock', dir: 'desc' });

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://apisaleeasy.psisat.com/api/product-inventory/unsold-stock');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'โหลดข้อมูลไม่สำเร็จ');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'last_sale_date' ? 'desc' : 'desc' }
    );
  };

  const filteredItems = () => {
    if (!data) return [];
    const filtered = data.items
      .filter(r => r.sale_status === activeTab)
      .filter(r => {
        if (!search) return true;
        const q = search.toLowerCase();
        return r.item_code.toLowerCase().includes(q) || r.item_name.toLowerCase().includes(q);
      });

    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'last_sale_date') {
        aVal = aVal ? new Date(aVal).getTime() : -Infinity;
        bVal = bVal ? new Date(bVal).getTime() : -Infinity;
      } else {
        aVal = aVal ?? '';
        bVal = bVal ?? '';
      }

      if (aVal < bVal) return sortConfig.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const exportExcel = () => {
    if (!data) return;
    const rows = data.items.map(r => ({
      'รหัสสินค้า': r.item_code,
      'ชื่อสินค้า': r.item_name,
      'กลุ่ม': r.group_product,
      'Stock คงเหลือ': r.stock,
      'ใบขายทั้งหมด': r.invoice_count,
      'จำนวนที่ขายได้': r.total_sold,
      'ขายครั้งแรก': r.first_sale_date ? new Date(r.first_sale_date).toLocaleDateString('th-TH') : '-',
      'ขายล่าสุด': r.last_sale_date ? new Date(r.last_sale_date).toLocaleDateString('th-TH') : 'ไม่เคยขาย',
      'สถานะ': STATUS_LABEL[r.sale_status]?.label || r.sale_status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'สินค้าที่ไม่ได้ถูกขาย');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'unsold_products_thungkhok.xlsx');
  };

  const items = filteredItems();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">สินค้าที่ไม่ได้ถูกขาย</h2>
          <p className="text-gray-500 text-sm mt-1">คลัง Thungkhok — ตรวจสอบจาก Invoice จริง</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </button>
          <button
            onClick={exportExcel}
            disabled={!data}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mr-3" />
          กำลังโหลดข้อมูล...
        </div>
      )}

      {data && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-sm text-gray-500">SKU ทั้งหมด (มี stock)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{data.summary.total_sku.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">stock รวม {data.summary.total_stock.toLocaleString()} ชิ้น</p>
            </div>
            <div className="bg-red-50 rounded-xl border border-red-100 p-4">
              <p className="text-sm text-red-600">ไม่เคยขายเลย</p>
              <p className="text-2xl font-bold text-red-700 mt-1">{data.summary.never_sold_sku.toLocaleString()} SKU</p>
              <p className="text-xs text-red-400 mt-1">stock {data.summary.never_sold_stock.toLocaleString()} ชิ้น</p>
            </div>
            <div className="bg-yellow-50 rounded-xl border border-yellow-100 p-4">
              <p className="text-sm text-yellow-600">หยุดขายมา &gt;2 ปี</p>
              <p className="text-2xl font-bold text-yellow-700 mt-1">{data.summary.stale_sku.toLocaleString()} SKU</p>
              <p className="text-xs text-yellow-400 mt-1">stock {data.summary.stale_stock.toLocaleString()} ชิ้น</p>
            </div>
            <div className="bg-green-50 rounded-xl border border-green-100 p-4">
              <p className="text-sm text-green-600">ยังขายอยู่</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{data.summary.active_sku.toLocaleString()} SKU</p>
              <p className="text-xs text-green-400 mt-1">stock {data.summary.active_stock.toLocaleString()} ชิ้น</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl border">
            <div className="flex border-b">
              {TAB_OPTIONS.map(tab => {
                const Icon = tab.icon;
                const count = data.items.filter(r => r.sale_status === tab.key).length;
                return (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setSearch(''); }}
                    className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? `border-blue-600 ${tab.color}`
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    <span className="ml-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="p-4 border-b">
              <input
                type="text"
                placeholder="ค้นหารหัสหรือชื่อสินค้า..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <span className="ml-3 text-sm text-gray-400">{items.length} รายการ</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    {COLUMNS.map(col => {
                      const isRight = ['stock', 'invoice_count', 'total_sold'].includes(col.key);
                      const isActive = sortConfig.key === col.key;
                      const Icon = isActive
                        ? sortConfig.dir === 'asc' ? ChevronUp : ChevronDown
                        : null;
                      return (
                        <th
                          key={col.key}
                          onClick={() => toggleSort(col.key)}
                          className={`px-4 py-3 cursor-pointer select-none hover:bg-gray-100 ${isRight ? 'text-right' : 'text-left'} ${isActive ? 'text-blue-600' : ''}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {!isRight && col.label}
                            {Icon && <Icon className="w-3 h-3" />}
                            {isRight && col.label}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-gray-400">ไม่พบข้อมูล</td>
                    </tr>
                  )}
                  {items.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-medium text-gray-800">{row.item_code}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={row.item_name}>{row.item_name}</td>
                      <td className="px-4 py-3">
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-mono">{row.group_product || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{Number(row.stock).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{Number(row.invoice_count).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{Number(row.total_sold).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {row.last_sale_date
                          ? new Date(row.last_sale_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })
                          : <span className="text-red-400">ไม่มีข้อมูล</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UnsoldProductsComponent;
